import { 
  doc, 
  getDoc, 
  setDoc, 
  updateDoc, 
  collection,
  query,
  where,
  getDocs,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../../firebase';
import { getCompanyIdForUser, findAccountIdByEmail, getPrimaryUserIdForAccount } from '../firestoreService';

const COLLECTION_NAME = 'userMissionVisionValues';

const buildDefaultMVV = () => ({
  mission: "To tangibly improve the lives of our clients and employees.",
  vision: "To grow a legacy of quality, teamwork, and innovation that reshapes the remodeling experience.",
  values: [
    { title: "Customer Focus", description: "We put our customers at the center of everything we do." },
    { title: "Innovation", description: "We constantly seek new ways to improve and evolve." },
    { title: "Integrity", description: "We operate with honesty, transparency, and ethical standards." },
    { title: "Excellence", description: "We strive for the highest quality in all our endeavors." }
  ],
  lastUpdated: new Date().toISOString()
});

// Get mission, vision, and values for a specific user
export const getUserMVV = async (userId) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return docSnap.data();
    } else {
      // Create default structure and save it to Firestore
      const defaultData = {
        ...buildDefaultMVV(),
        userId: userId
      };
      
      // Save the default data to create the document
      await setDoc(docRef, defaultData);
      return defaultData;
    }
  } catch (error) {
    console.error('Error getting user MVV:', error);
    throw error;
  }
};

// Get shared MVV for a company based on a user context.
// Returns { data, ownerId } where ownerId is typically the company/account id.
export const getSharedMVVForUser = async (userId, email = null) => {
  try {
    // Resolve company/account id for the user
    let companyId = await getCompanyIdForUser(userId);
    if (!companyId && email) {
      try {
        companyId = await findAccountIdByEmail(email, 'Clients');
      } catch (findErr) {
        console.error('Error finding account by email for MVV:', findErr);
      }
    }

    // If we cannot resolve a company id, fall back to user-scoped MVV
    if (!companyId) {
      const data = await getUserMVV(userId);
      return { data, ownerId: userId };
    }

    const data = await getMVVByOwnerId(companyId);
    return { data, ownerId: companyId };
  } catch (error) {
    console.error('Error getting shared MVV for user/company:', error);
    const data = await getUserMVV(userId);
    return { data, ownerId: userId };
  }
};

// Load MVV by owner id (companyId or userId). Uses company doc first, then primary user fallback.
export const getMVVByOwnerId = async (ownerId) => {
  const docRef = doc(db, COLLECTION_NAME, ownerId);
  const snap = await getDoc(docRef);
  if (snap.exists()) {
    return snap.data();
  }
  // Fallback: if this looks like an account id, try primary user's MVV and copy to this doc
  try {
    const primaryUserId = await getPrimaryUserIdForAccount(ownerId);
    if (primaryUserId && primaryUserId !== ownerId) {
      const primaryRef = doc(db, COLLECTION_NAME, primaryUserId);
      const primarySnap = await getDoc(primaryRef);
      if (primarySnap.exists()) {
        const primaryData = primarySnap.data();
        const toSave = {
          ...primaryData,
          userId: ownerId,
          lastUpdated: new Date().toISOString()
        };
        await setDoc(docRef, toSave, { merge: true });
        return toSave;
      }
    }
  } catch (e) {
    console.error('MVV primary fallback error:', e);
  }
  const defaultData = { ...buildDefaultMVV(), userId: ownerId };
  await setDoc(docRef, defaultData);
  return defaultData;
};

// Save or update mission, vision, and values for a user
export const saveUserMVV = async (userId, mvvData) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const dataToSave = {
      ...mvvData,
      lastUpdated: new Date().toISOString(),
      userId: userId
    };
    
    await setDoc(docRef, dataToSave, { merge: true });
    return dataToSave;
  } catch (error) {
    console.error('Error saving user MVV:', error);
    throw error;
  }
};

// Update specific sections of MVV
export const updateUserMVVSection = async (userId, section, data) => {
  try {
    const docRef = doc(db, COLLECTION_NAME, userId);
    const updateData = {
      [section]: data,
      lastUpdated: new Date().toISOString(),
      userId: userId
    };
    
    // Use setDoc with merge: true to create document if it doesn't exist
    await setDoc(docRef, updateData, { merge: true });
    return updateData;
  } catch (error) {
    console.error('Error updating user MVV section:', error);
    throw error;
  }
};

// Delete a specific value from the values array
export const deleteUserValue = async (userId, valueIndex) => {
  try {
    const currentData = await getUserMVV(userId);
    // Ensure values is an array, fallback to empty array if not
    const currentValues = Array.isArray(currentData.values) ? currentData.values : [];
    const updatedValues = currentValues.filter((_, index) => index !== valueIndex);
    
    // Use setDoc with merge: true to ensure document exists
    const docRef = doc(db, COLLECTION_NAME, userId);
    const updateData = {
      values: updatedValues,
      lastUpdated: new Date().toISOString(),
      userId: userId
    };
    
    await setDoc(docRef, updateData, { merge: true });
    return updatedValues;
  } catch (error) {
    console.error('Error deleting user value:', error);
    throw error;
  }
};

// Add a new value to the values array
export const addUserValue = async (userId, newValue) => {
  try {
    const currentData = await getUserMVV(userId);
    // Ensure values is an array, fallback to empty array if not
    const currentValues = Array.isArray(currentData.values) ? currentData.values : [];
    const updatedValues = [...currentValues, newValue];
    
    // Use setDoc with merge: true to ensure document exists
    const docRef = doc(db, COLLECTION_NAME, userId);
    const updateData = {
      values: updatedValues,
      lastUpdated: new Date().toISOString(),
      userId: userId
    };
    
    await setDoc(docRef, updateData, { merge: true });
    return updatedValues;
  } catch (error) {
    console.error('Error adding user value:', error);
    throw error;
  }
};

// Update a specific value in the values array
export const updateUserValue = async (userId, valueIndex, updatedValue) => {
  try {
    const currentData = await getUserMVV(userId);
    // Ensure values is an array, fallback to empty array if not
    const currentValues = Array.isArray(currentData.values) ? currentData.values : [];
    const updatedValues = [...currentValues];
    updatedValues[valueIndex] = updatedValue;
    
    // Use setDoc with merge: true to ensure document exists
    const docRef = doc(db, COLLECTION_NAME, userId);
    const updateData = {
      values: updatedValues,
      lastUpdated: new Date().toISOString(),
      userId: userId
    };
    
    await setDoc(docRef, updateData, { merge: true });
    return updatedValues;
  } catch (error) {
    console.error('Error updating user value:', error);
    throw error;
  }
};
